import styled from '@emotion/styled'
import { formatHumanReadable, sameDay } from '@pubstack/common/src/date'
import { GranularityToUnit, NewReport, Report, ReportNameType, ReportPeriod, ReportsConfiguration, reportsConfiguration } from '@pubstack/common/src/report'
import { isPubstackUserRole } from '@pubstack/common/src/rolesAndFeatures'
import { lowerCaseFirstLetter } from '@pubstack/common/src/stringUtils'
import { DateTime, Interval } from 'luxon'
import { FunctionComponent, useMemo, useState } from 'react'
import { useCreateReport, useDeleteReport, useReports } from '~/api/app/report.api'
import { useSites } from '~/api/app/sites.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Sizes } from '~/assets/style/tokens'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { BaseCombobox } from '~/components/Combobox'
import Datepicker from '~/components/Datepicker'
import { IntervalWithLabel } from '~/components/datepicker.utils'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'
import { PageContent } from '~/components/PageContent'
import { Popover } from '~/components/Popover'
import { Spinner } from '~/components/Spinner'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureReportsSelector, ReportSelectorOption } from '~/modules/reports/PureReportsSelector'
import { PureReportsTable } from '~/modules/reports/PureReportsTable'

const SubTitle = styled.div`
  ${Fonts.P1};
  flex: 1 1 auto;
`

const SubTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  margin-top: ${Sizes[20]};
  margin-bottom: ${Sizes[16]};
`

const CreateNewReportZone = styled.div`
  margin-top: ${Sizes[16]};
  margin-bottom: 2.5rem;
  background-color: ${Colors.White};
  border: 1px solid ${Colors.Platinum};
  border-radius: ${Sizes[4]};
  padding: ${Sizes[16]} ${Sizes[12]};

  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;

  ${Button} {
    margin: 0 0.5rem;
  }
  
`

const SpanWithMargin = styled.span`
  margin: 0 0.5rem;
`

const CreateReportButton = styled(Button)``

const CreateNewReportSelectionWrapper = styled.div`
  flex: 1 1 auto;
  ${Fonts.P3};
  display: flex;
  align-items: center;
`

const getIntervalAsString = (selectedRange: IntervalWithLabel) => {
  if (selectedRange.label === 'custom') {
    return sameDay(selectedRange.interval.start, selectedRange.interval.end)
      ? formatHumanReadable(selectedRange.interval.start)
      : `${formatHumanReadable(selectedRange.interval.start)} to ${formatHumanReadable(selectedRange.interval.end)}`
  }
  return lowerCaseFirstLetter(selectedRange.label)
}

type ReportTimeSelectorProps = {
  onUpdate: (interval: IntervalWithLabel) => void
  selectedRange?: IntervalWithLabel
  today: DateTime
  selectableInterval: Interval
  maxNumberOfDays: number
  initialSelectedInterval: {
    label: string
    interval: Interval
  }
}
const ReportTimeSelector: FunctionComponent<ReportTimeSelectorProps> = ({ today, onUpdate, selectableInterval, selectedRange, maxNumberOfDays, initialSelectedInterval }) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const timePeriodLabelBefore = selectedRange ? (sameDay(selectedRange.interval.start, selectedRange.interval.end) || selectedRange.label !== 'custom' ? 'on' : 'from') : 'on'
  const intervalLabel = selectedRange ? getIntervalAsString(selectedRange) : 'Time period'
  const disclaimer = useMemo(() => {
    const text = `${formatHumanReadable(selectableInterval.start)} and ${formatHumanReadable(selectableInterval.end.minus({ days: 1 }))}`
    if (maxNumberOfDays) {
      return `You can select up to ${maxNumberOfDays} days between ${text}`
    } else {
      return `You can select any date range between ${text}`
    }
  }, [maxNumberOfDays, selectableInterval])
  return (
    <>
      {timePeriodLabelBefore}
      <Popover
        trigger={
          <Button variant={'secondary'} trackingLabel={'ana-reports-date-picker-view'}>
            {intervalLabel}
          </Button>
        }
        open={datePickerOpen}
        setOpen={setDatePickerOpen}
        align={'center'}
      >
        <Datepicker
          selectableInterval={selectableInterval}
          initialSelectedInterval={initialSelectedInterval}
          maxNumberOfDayInTimerangeSelected={maxNumberOfDays}
          today={today}
          disclaimer={disclaimer}
          onTimerangeSelected={(selection) => {
            onUpdate({ label: selection.label, interval: Interval.fromDateTimes(selection.interval.start.startOf('day'), selection.interval.end.endOf('day')) })
            setDatePickerOpen(false)
          }}
        />
      </Popover>
    </>
  )
}

type ReportConfig = ReportsConfiguration[keyof ReportsConfiguration]
const getFormattedPeriodString = (reportPeriod: ReportPeriod): string => {
  if (reportPeriod.unit === 1 && reportPeriod.granularity === 'days') {
    return 'yesterday'
  }
  return `last ${reportPeriod.unit === 1 ? reportPeriod.granularity.slice(0, -1) : `${reportPeriod.unit} ${reportPeriod.granularity}`}`
}

const getSelectableInterval = (reportConfig: ReportConfig | undefined, today: DateTime) => {
  const todayUtc = today.startOf('day')
  if (!reportConfig) {
    return Interval.fromDateTimes(today, today)
  } else {
    let beginInterval = today.minus({ days: reportConfig.maxRetentionInDays })
    const beginIntervalUtc = todayUtc.minus({ days: reportConfig.maxRetentionInDays })
    // here, we want to reduce the interval to the minimum between what the local user wants, and what data range we have.
    // for example, we have access to data until 2020-09-16.
    // If the user is in the timezone Europe/Paris (GMT +2) he cannot query the day 2020-09-16 because part of this day is the end of the UTC 2020-09-15.
    if (beginInterval < beginIntervalUtc) {
      beginInterval = beginIntervalUtc.toLocal()
    }
    let endInterval = today
    if (endInterval > todayUtc) {
      endInterval = todayUtc.toLocal()
    }
    return Interval.fromDateTimes(beginInterval, endInterval)
  }
}

const getInitialSelectedInterval = (reportConfig: ReportConfig | undefined, selectableInterval: Interval, today: DateTime) => {
  if (!reportConfig) {
    return {
      label: '',
      interval: Interval.fromDateTimes(today, today),
    }
  }
  const { defaultSelectedPeriod } = reportConfig
  const start = selectableInterval.end.minus({ [defaultSelectedPeriod.granularity]: defaultSelectedPeriod.unit }).startOf(GranularityToUnit[defaultSelectedPeriod.granularity])
  return {
    label: getFormattedPeriodString(defaultSelectedPeriod),
    interval: Interval.fromDateTimes(start, start.plus({ [defaultSelectedPeriod.granularity]: defaultSelectedPeriod.unit })),
  }
}

const DeleteReportModal: FunctionComponent<{ reportId: string }> = ({ reportId }) => {
  const { close } = useGlobalModal()
  const { mutateAsync: deleteReport } = useDeleteReport(reportId)
  return (
    <Modal.Content>
      <Modal.Title>Delete ?</Modal.Title>

      <Modal.Body>You won't be able to view this report in Pubstack anymore. Do you confirm your choice?</Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'ana-reports-delete-cancel'}>
          Cancel
        </Button>
        <Button
          variant={'negative'}
          iconName={'delete'}
          onClick={async () => {
            await deleteReport()
            close()
          }}
          trackingLabel={'ana-reports-delete-confirm'}
        >
          Delete
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

export const ReportsPage: FunctionComponent = () => {
  const today = DateTime.now()
  const user = useAuthenticatedUser()
  const scopeId = user.currentScopeId
  const { data: reports, refetch, isLoading, isRefetching } = useReports()
  const [dropdownState, setDropdownState] = useState<{ value?: string; label: string }>({ value: undefined, label: '' })
  const { data: sites, isLoading: sitesLoading } = useSites(scopeId, dropdownState.label || undefined)
  const breadcrumbs = useBreadcrumbs()
  const modal = useGlobalModal()

  const [reportSelectedIndex, setReportSelectedIndex] = useState<number | undefined>(undefined)
  const [selectedInterval, setSelectedInterval] = useState<IntervalWithLabel | undefined>(undefined)

  const { mutateAsync: createReport, isPending } = useCreateReport()

  const isPubstackRole = isPubstackUserRole(user.userRoles)
  const options: ReportSelectorOption[] = (Object.entries(reportsConfiguration) as [ReportNameType, ReportConfig][])
    .filter(([, { pubstackOnly }]) => (pubstackOnly ? isPubstackRole : true))
    .map(([value, { title, description }]) => ({
      value,
      title,
      description,
      trackingLabel: `ana-reports-type-${normalizeAsTrackingLabel(title)}-set`,
    }))
  const reportSelected = reportSelectedIndex !== undefined ? options[reportSelectedIndex] : undefined
  const reportConfig = reportSelected ? reportsConfiguration[reportSelected.value] : undefined
  const isCreateReportDisabled = user.getScopeRole() === 'demo' || !reportSelected || !selectedInterval || (reportConfig?.shouldSelectASite ? !dropdownState.value : false)
  const selectableInterval = getSelectableInterval(reportConfig, today)
  const initialSelectedInterval = selectedInterval
    ? { label: selectedInterval.label ?? '', interval: Interval.fromDateTimes(selectedInterval.interval.start.startOf('day'), selectedInterval.interval.end.plus({ days: 1 }).endOf('day')) }
    : getInitialSelectedInterval(reportConfig, selectableInterval, today)

  const onRowClick = (report: Report) => {
    if (report.status === 'CREATED' && report.url) {
      const link = document.createElement('a')
      link.setAttribute('href', report.url)
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      link.remove()
    }
  }

  const onTemplateChange = (index: number) => {
    setSelectedInterval(undefined)
    setReportSelectedIndex(index)

    const reportSelected = options[index]
    const reportConfig = reportsConfiguration[reportSelected.value]
    const selectableInterval = getSelectableInterval(reportConfig, today)
    const initialSelectedInterval = getInitialSelectedInterval(reportConfig, selectableInterval, today)
    const selectedInterval = Interval.fromDateTimes(initialSelectedInterval.interval.start, initialSelectedInterval.interval.end.minus({ days: 1 }))
    setSelectedInterval({ label: getFormattedPeriodString(reportConfig.defaultSelectedPeriod), interval: selectedInterval })
  }

  const onRefreshClick = async () => {
    await refetch()
  }

  const onCreateReportClick = async (value: ReportNameType, interval: Interval) => {
    const report: NewReport = {
      reportName: value,
      startDate: interval.start.startOf('day').toISO(),
      endDate: interval.end.endOf('day').toISO(),
      timezone: DateTime.local().zoneName,
      siteId: reportConfig?.shouldSelectASite ? dropdownState.value : undefined,
    }
    await createReport(report)
  }

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      <SubTitleWrapper>
        <SubTitle>Create new report</SubTitle>
      </SubTitleWrapper>
      <CreateNewReportZone>
        <CreateNewReportSelectionWrapper>
          Create <PureReportsSelector options={options} value={reportSelected} onChange={(_, index) => onTemplateChange(index)} />
          {reportConfig && (
            <ReportTimeSelector
              onUpdate={setSelectedInterval}
              today={today}
              maxNumberOfDays={reportConfig.maxSelectableIntervalInDays}
              selectableInterval={selectableInterval}
              initialSelectedInterval={initialSelectedInterval}
              selectedRange={selectedInterval}
            />
          )}
          {reportConfig?.shouldSelectASite && (
            <>
              <SpanWithMargin>for site</SpanWithMargin>
              <BaseCombobox
                value={dropdownState.label}
                onChange={(val) => {
                  setDropdownState({ value: undefined, label: val })
                }}
                options={sites.map((site) => ({
                  label: site.name,
                  value: site.id,
                  trackingLabel: `ana-reports-dropdown-site-${site.id}-set`,
                }))}
                optionsLoading={sitesLoading}
                onSelect={(option) => {
                  setDropdownState({ label: option.label, value: option.value })
                }}
                label="Type to search a site"
              />
            </>
          )}
        </CreateNewReportSelectionWrapper>
        {isPending || isLoading ? (
          <Spinner isColored={false} />
        ) : (
          <CreateReportButton
            data-ph-capture-attribute-report-created={reportSelected?.title}
            variant={'primary'}
            iconName={'document'}
            disabled={isCreateReportDisabled}
            onClick={() => reportSelected && selectedInterval && onCreateReportClick(reportSelected?.value, selectedInterval?.interval)}
            trackingLabel={'ana-reports-new'}
          >
            Create report
          </CreateReportButton>
        )}
      </CreateNewReportZone>

      <SubTitleWrapper>
        <SubTitle>Last reports</SubTitle>
        <Button variant={'tertiary'} iconName={'refresh'} onClick={onRefreshClick} trackingLabel={'ana-reports-refresh'}>
          Refresh
        </Button>
      </SubTitleWrapper>
      <PureReportsTable isLoading={isLoading || isRefetching} onRowClick={onRowClick} onDeleteClick={(report) => modal.open(DeleteReportModal, { reportId: report.id })} reports={reports} />
    </PageContent>
  )
}
